import posthog from 'posthog-js';
import { ValuesType } from 'utility-types';

import PlayersManager from '~/modules/players/players-manager';
import storage from '~/modules/utils/storage';
import { MicSetupPreference, MicSetupPreferenceSetting } from '~/routes/settings/settings-state';

const LAST_SELECTED_KEY = 'Previously selected input type';

/**
 * Closing the mic setup IS setting it up, by whichever exit — the `← Done` button, Backspace, or a
 * click on the backdrop behind the modal. `game-settings.tsx` gates its Play row on
 * `MicSetupPreferenceSetting`, so an exit that did not write it left a room that had just assigned
 * four microphones still counting as never set up.
 *
 * A module function rather than a method on the view because the backdrop click never reaches the
 * view at all — `Modal` owns it.
 *
 * Returns what ended up behind the slots, for the analytics that read it; a room that set nothing
 * up still confirmed the manual screen, which is what `advanced` has always meant.
 */
export default function commitMicSetup(): ValuesType<typeof MicSetupPreference> {
  // "Player 3 sits out until a mic is added" has to be true of the game too, not just of this
  // screen - see `dropUnassignedPlayers` for why a slot left on `Dummy` is worse than an empty one.
  PlayersManager.dropUnassignedPlayers();

  const assigned = PlayersManager.getInputs().map((input) => input.source);
  const preference: ValuesType<typeof MicSetupPreference> = assigned.includes('Remote Microphone')
    ? 'remoteMics'
    : assigned.includes('Microphone')
      ? 'built-in'
      : 'advanced';

  MicSetupPreferenceSetting.set(preference);
  storage.local.setItem(LAST_SELECTED_KEY, preference);
  posthog.capture('sourcePreferenceSet', { source: preference });

  return preference;
}
